import { Injectable, signal, computed } from '@angular/core';
import { type FeatureFlags } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly storageKey = 'feature-flag-overrides';
  private readonly flags = signal<FeatureFlags>(this.loadFlags());

  readonly allFlags = computed(() => this.flags());

  constructor() {
    window.addEventListener('storage', () => {
      this.flags.set(this.loadFlags());
    });
  }

  isEnabled(flag: keyof FeatureFlags): boolean {
    return this.flags()[flag];
  }

  override(flag: keyof FeatureFlags, value: boolean): void {
    const overrides = this.getOverrides();
    overrides[flag] = value;
    localStorage.setItem(this.storageKey, JSON.stringify(overrides));
    this.flags.set(this.loadFlags());
  }

  removeOverride(flag: keyof FeatureFlags): void {
    const overrides = this.getOverrides();
    delete overrides[flag];
    localStorage.setItem(this.storageKey, JSON.stringify(overrides));
    this.flags.set(this.loadFlags());
  }

  clearOverrides(): void {
    localStorage.removeItem(this.storageKey);
    this.flags.set(this.loadFlags());
  }

  private loadFlags(): FeatureFlags {
    const overrides = this.getOverrides();
    return {
      // GTLC environment doesn't have features - default to empty object
      ...({} as FeatureFlags),
      ...overrides,
    };
  }

  private getOverrides(): Partial<FeatureFlags> {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }
}
